import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MainLayout from '../../../layouts/MainLayout';
import { Badge, Button, Segmented, Space, message, Card } from 'antd';
import { 
  PlusOutlined, 
  FileExcelOutlined, 
  ImportOutlined,
  UnorderedListOutlined,
  UserOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import AttendanceListView from '../../../components/hr/attendance/AttendanceListView';
import AttendanceByMemberView from '../../../components/hr/attendance/AttendanceByMemberView';
import AttendanceByHourView from '../../../components/hr/attendance/AttendanceByHourView';
import AttendanceByLocationView from '../../../components/hr/attendance/AttendanceByLocationView';
import AttendanceNeedsReviewView from '../../../components/hr/attendance/AttendanceNeedsReviewView';
import { attendanceApi, departmentsApi, designationsApi, employeesApi } from '../../../utils/api';
import moment from 'moment-jalaali';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useAuth } from '../../../contexts/AuthContext';
import { useListReturnState } from '../../../hooks/useListReturnState';
import { readCachedAuthUserId } from '../../../utils/cachedAuthUser';

const toFaDigits = (value) => {
  if (value == null || value === '') return '۰';
  return String(value).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);
};

const Attendance = () => {
  const { canAdd } = useResourcePermissions('attendance');
  const { user: authUser } = useAuth();
  const [viewMode, setViewMode] = useState('list'); // 'list', 'member', 'hour', 'location'
  const defaultUserId = useMemo(() => {
    const id = authUser?.id ?? readCachedAuthUserId();
    return id != null ? String(id) : null;
  }, [authUser?.id]);
  const [filters, setFilters] = useState(() => {
    const cachedId = readCachedAuthUserId();
    const selfId = cachedId != null ? String(cachedId) : null;
    return {
      year: moment().format('jYYYY'),
      month: moment().format('jM').padStart(2, '0'),
      monthName: (() => {
        const m = moment().format('jM');
        const map = {
          '1': 'فروردین', '2': 'اردیبهشت', '3': 'خرداد', '4': 'تیر', '5': 'مرداد', '6': 'شهریور',
          '7': 'مهر', '8': 'آبان', '9': 'آذر', '10': 'دی', '11': 'بهمن', '12': 'اسفند',
        };
        return map[m] || 'آذر';
      })(),
      department: 'all',
      designation: 'all',
      // employee_id پیش‌فرض = کاربر لاگین‌شده
      employee: selfId || 'all',
      userId: selfId || 'all',
      memberUserId: selfId,
      pageSize: 30,
    };
  });
  const [loading, setLoading] = useState(false);
  const [listData, setListData] = useState({});
  const [listMeta, setListMeta] = useState({});
  const [memberData, setMemberData] = useState(null);
  const [hourData, setHourData] = useState(null);
  const [deptOptions, setDeptOptions] = useState([]);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [needsReviewTotal, setNeedsReviewTotal] = useState(0);

  const captureCurrentListState = useCallback(
    () => ({
      viewMode,
      filters,
    }),
    [viewMode, filters],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.viewMode) setViewMode(restored.viewMode);
    if (restored.filters) setFilters(restored.filters);
  });

  const jMonthToNumber = (jm) => {
    const m = Number(jm);
    if (!Number.isNaN(m)) return m.toString().padStart(2, '0');
    const map = {
      'فروردین': '01', 'اردیبهشت': '02', 'خرداد': '03', 'تیر': '04', 'مرداد': '05', 'شهریور': '06',
      'مهر': '07', 'آبان': '08', 'آذر': '09', 'دی': '10', 'بهمن': '11', 'اسفند': '12',
    };
    return map[jm] || moment().format('jM').padStart(2, '0');
  };

  const jMonthNumberToName = (num) => {
    const map = {
      '01': 'فروردین', '1': 'فروردین',
      '02': 'اردیبهشت', '2': 'اردیبهشت',
      '03': 'خرداد', '3': 'خرداد',
      '04': 'تیر', '4': 'تیر',
      '05': 'مرداد', '5': 'مرداد',
      '06': 'شهریور', '6': 'شهریور',
      '07': 'مهر', '7': 'مهر',
      '08': 'آبان', '8': 'آبان',
      '09': 'آذر', '9': 'آذر',
      '10': 'دی',
      '11': 'بهمن',
      '12': 'اسفند',
    };
    return map[num] || 'آذر';
  };

  const loadOptions = async () => {
    try {
      const [deptRes, desRes, empRes] = await Promise.all([
        departmentsApi.getAll(),
        designationsApi.getAll(),
        employeesApi.getAll(),
      ]);
      setDeptOptions(deptRes.data || []);
      setDesignationOptions(desRes.data || []);
      setEmployeeOptions(empRes.data || empRes || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchList = async (params = {}) => {
    setLoading(true);
    try {
      const year = params.year || filters.year;
      const month = params.month || filters.month;
      const res = await attendanceApi.getAll({
        year,
        month,
        department: params.department ?? filters.department,
        designation: params.designation ?? filters.designation,
        employee_id: params.employee ?? filters.employee,
        userId: params.userId ?? filters.userId,
      });
      const data = res.data || {};
      setListData(data);
      setListMeta(data.meta || {});
    } catch (error) {
      console.error(error);
      message.error('خطا در دریافت حضور و غیاب');
    } finally {
      setLoading(false);
    }
  };

  const resolveMemberUserId = (params = {}) => {
    const id = params.memberUserId ?? params.userId ?? filters.memberUserId ?? filters.userId;
    if (!id || id === 'all') return null;
    return String(id);
  };

  const fetchByMember = async (params = {}) => {
    const userId = resolveMemberUserId(params);
    if (!userId) {
      setMemberData(null);
      message.warning('لطفاً یک کارمند را انتخاب کنید');
      return;
    }
    setLoading(true);
    try {
      const year = params.year || filters.year;
      const month = params.month || filters.month;
      const res = await attendanceApi.getByMember({ userId, year, month });
      const payload = res?.data || res || {};
      setMemberData(payload);
    } catch (error) {
      console.error(error);
      message.error('خطا در دریافت گزارش بر اساس عضو');
    } finally {
      setLoading(false);
    }
  };

  const fetchByHour = async (params = {}) => {
    setLoading(true);
    try {
      const year = params.year || filters.year;
      const month = params.month || filters.month;
      const res = await attendanceApi.getByHour({
        year,
        month,
        userId: params.userId ?? filters.userId,
        department: params.department ?? filters.department,
        designation: params.designation ?? filters.designation,
      });
      setHourData(res.data || {});
    } catch (error) {
      console.error(error);
      message.error('خطا در دریافت گزارش بر اساس ساعت');
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (payload = {}) => {
    const next = {
      ...filters,
      ...payload,
    };
    if (payload.memberUserId != null) {
      next.memberUserId = payload.memberUserId;
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'employee') || Object.prototype.hasOwnProperty.call(payload, 'userId')) {
      next._employeeTouched = payload._employeeTouched === false ? false : true;
      if (payload.employee == null || payload.employee === '') {
        next.employee = 'all';
      }
      if (payload.userId == null || payload.userId === '') {
        next.userId = 'all';
      }
    }
    if (payload.monthName) {
      next.monthName = payload.monthName;
      next.month = jMonthToNumber(payload.monthName);
    } else if (payload.month) {
      next.month = jMonthToNumber(payload.month);
      next.monthName = jMonthNumberToName(next.month);
    }
    setFilters(next);
    if (viewMode === 'list') fetchList(next);
    if (viewMode === 'member') fetchByMember(next);
    if (viewMode === 'hour') fetchByHour(next);
  };

  useEffect(() => {
    if (!defaultUserId) return undefined;
    if (filters._employeeTouched) return undefined;

    const needsSelf =
      !filters.memberUserId ||
      !filters.employee ||
      filters.employee === 'all' ||
      !filters.userId ||
      filters.userId === 'all';

    if (!needsSelf) return undefined;

    const next = {
      ...filters,
      memberUserId: filters.memberUserId || defaultUserId,
      employee: !filters.employee || filters.employee === 'all' ? defaultUserId : filters.employee,
      userId: !filters.userId || filters.userId === 'all' ? defaultUserId : filters.userId,
    };
    setFilters(next);
    if (viewMode === 'list') fetchList(next);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultUserId]);

  const loadNeedsReviewBadge = useCallback(async () => {
    try {
      const res = await attendanceApi.getNeedsReviewSummary({
        year: filters.year,
        month: filters.month,
      });
      const data = res?.data || res || {};
      const total = data.counts?.total_issues ?? 0;
      setNeedsReviewTotal(Number(total) || 0);
    } catch (e) {
      console.warn('needs-review summary:', e?.message || e);
    }
  }, [filters.year, filters.month]);

  const handleNeedsReviewCounts = useCallback((counts) => {
    setNeedsReviewTotal(Number(counts?.total_issues) || 0);
  }, []);

  const handleRegisterAttendance = () => {
    navigateToDetail('/hr/attendance/register');
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      await attendanceApi.exportExcel({
        year: filters.year,
        month: filters.month,
        id: filters.employee || filters.userId || 'all',
        department: filters.department || 'all',
        designation: filters.designation || 'all',
      });
      message.success('خروجی اکسل در حال دانلود است');
    } catch (error) {
      console.error('Export attendance:', error);
      message.error(error?.message || 'خطا در خروجی اکسل');
    } finally {
      setExporting(false);
    }
  };

  const handleImport = () => {
    message.info('قابلیت وارد کردن در حال توسعه است');
  };

  useEffect(() => {
    loadOptions();
    fetchList();
    loadNeedsReviewBadge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (viewMode !== 'needs-review') {
      loadNeedsReviewBadge();
    }
  }, [filters.year, filters.month, viewMode, loadNeedsReviewBadge]);

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                <ClockCircleOutlined className="text-blue-600 dark:text-blue-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">حضور و غیاب</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">مشاهده حضور و غیاب کارکنان</p>
              </div>
            </div>
            <Space size="middle" wrap>
              {canAdd ? (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleRegisterAttendance}
                  size="middle"
                  className="bg-green-600 hover:bg-green-700 border-0 shadow-md hover:shadow-lg transition-all"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  ثبت حضور
                </Button>
              ) : null}
              <Button
                icon={<FileExcelOutlined />}
                onClick={handleExportExcel}
                loading={exporting}
                size="middle"
                style={{ fontFamily: 'CustomFont' }}
              >
                خروجی اکسل
              </Button>
              <Button
                icon={<ImportOutlined />}
                onClick={handleImport}
                size="middle"
                style={{ fontFamily: 'CustomFont' }}
              >
                وارد کردن
              </Button>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            {/* دکمه‌های تغییر حالت نمایش */}
            <div className="mb-6">
              <Segmented
                value={viewMode}
                onChange={(v) => {
                  setViewMode(v);
                  if (v === 'list') fetchList();
                  if (v === 'member') {
                    const memberId = filters.memberUserId || defaultUserId;
                    if (memberId) {
                      fetchByMember({ memberUserId: memberId });
                    } else {
                      message.warning('لطفاً یک کارمند را انتخاب کنید');
                    }
                  }
                  if (v === 'hour') fetchByHour();
                }}
                options={[
                  {
                    label: 'لیست',
                    value: 'list',
                    icon: <UnorderedListOutlined />,
                  },
                  {
                    label: 'بر اساس عضو',
                    value: 'member',
                    icon: <UserOutlined />,
                  },
                  {
                    label: 'بر اساس ساعت',
                    value: 'hour',
                    icon: <ClockCircleOutlined />,
                  },
                  {
                    label: 'بر اساس موقعیت',
                    value: 'location',
                    icon: <EnvironmentOutlined />,
                  },
                  {
                    label: (
                      <span className="inline-flex items-center gap-1.5" style={{ fontFamily: 'CustomFont' }}>
                        نیازمند بررسی
                        {needsReviewTotal > 0 ? (
                          <Badge
                            count={toFaDigits(needsReviewTotal)}
                            overflowCount={99}
                            style={{ backgroundColor: '#dc2626', boxShadow: 'none' }}
                            size="small"
                          />
                        ) : null}
                      </span>
                    ),
                    value: 'needs-review',
                    icon: <WarningOutlined />,
                  },
                ]}
                size="large"
                style={{ fontFamily: 'CustomFont' }}
              />
            </div>

            {/* نمایش بر اساس حالت انتخاب شده */}
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
              {viewMode === 'list' && (
                <AttendanceListView
                  loading={loading}
                  data={listData}
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  onRefresh={() => fetchList()}
                  departments={deptOptions}
                  designations={designationOptions}
                  employees={employeeOptions}
                  defaultEmployeeId={defaultUserId}
                />
              )}
              {viewMode === 'member' && (
                <AttendanceByMemberView
                  loading={loading}
                  data={memberData}
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  employees={employeeOptions}
                />
              )}
              {viewMode === 'hour' && (
                <AttendanceByHourView
                  loading={loading}
                  data={hourData}
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  departments={deptOptions}
                  designations={designationOptions}
                  employees={employeeOptions}
                />
              )}
              {viewMode === 'location' && <AttendanceByLocationView />}
              {viewMode === 'needs-review' && (
                <AttendanceNeedsReviewView
                  departments={deptOptions}
                  employees={employeeOptions}
                  year={filters.year}
                  month={filters.month}
                  onCountsChange={handleNeedsReviewCounts}
                />
              )}
            </div>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default Attendance;
